import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import DynamicPage from "./pages/DynamicPage";
import Nettbutikk from "./pages/Nettbutikk";
import ProductDetail from "./pages/ProductDetail";
import Arrangementer from "./pages/Arrangementer";
import FastgiverBekreftelse from "./pages/FastgiverBekreftelse";
import OrderConfirmation from "./pages/OrderConfirmation";
import CheckoutCancelled from "./pages/CheckoutCancelled";
import Styleguide from "./pages/Styleguide";
import NotFound from "./pages/NotFound";
import CookieConsent from "./components/CookieConsent";
import AdminLayout from "./components/admin/AdminLayout";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminAuthCallback from "./pages/admin/AdminAuthCallback";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminEvents from "./pages/admin/AdminEvents";
import AdminEventForm from "./pages/admin/AdminEventForm";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminOrderDetail from "./pages/admin/AdminOrderDetail";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminProductDetail from "./pages/admin/AdminProductDetail";
import AdminDonors from "./pages/admin/AdminDonors";
import AdminAnalytics from "./pages/admin/AdminAnalytics";

const queryClient = new QueryClient();

const AppContent = () => {
  return (
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/nettbutikk" element={<Nettbutikk />} />
      <Route path="/produkt/:handle" element={<ProductDetail />} />
      <Route path="/arrangementer" element={<Arrangementer />} />
      <Route path="/fastgiver-bekreftelse" element={<FastgiverBekreftelse />} />
      <Route path="/bekreftelse" element={<OrderConfirmation />} />
      <Route path="/checkout-avbrutt" element={<CheckoutCancelled />} />
      <Route path="/styleguide" element={<Styleguide />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/auth-callback" element={<AdminAuthCallback />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="events" element={<AdminEvents />} />
        <Route path="events/ny" element={<AdminEventForm />} />
        <Route path="events/:id" element={<AdminEventForm />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="orders/:id" element={<AdminOrderDetail />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="products/:id" element={<AdminProductDetail />} />
        <Route path="donors" element={<AdminDonors />} />
        <Route path="analytics" element={<AdminAnalytics />} />
      </Route>
      <Route path="/:slug" element={<DynamicPage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const ErrorFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background px-6">
    <div className="max-w-md text-center">
      <h1 className="font-heading text-2xl font-bold">Noe gikk galt</h1>
      <p className="mt-3 text-muted-foreground">
        Vi har fått beskjed om feilen. Last siden på nytt for å prøve igjen.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="mt-6 inline-flex rounded-full bg-primary px-6 py-2.5 font-body text-sm font-semibold text-primary-foreground hover:opacity-90"
      >
        Last på nytt
      </button>
    </div>
  </div>
);

const App = () => (
  <Sentry.ErrorBoundary fallback={ErrorFallback}>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <CookieConsent />
            <AppContent />
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </Sentry.ErrorBoundary>
);

export default App;
